import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building, Scale, Anchor, Home, CheckCircle, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const iconMap = {
  Building: Building,
  Scale: Scale,
  Anchor: Anchor,
  Home: Home
};

const defaultServices = [
  {
    id: "corporate",
    icon: "Building",
    title: "Corporate & Business Law",
    description: "Navigating complex mergers, acquisitions, and compliance with precision. We provide comprehensive legal solutions for businesses of all sizes in Jamaica.",
    image_url: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&q=80&w=1000",
    features: ["Company Formation & Registration", "Mergers & Acquisitions", "Corporate Governance", "Shareholder Agreements", "Regulatory Compliance", "Contract Negotiation & Drafting"]
  },
  {
    id: "civil",
    icon: "Scale",
    title: "Civil Litigation",
    description: "Defending your rights in high-stakes disputes and resolutions. Our litigation team brings decades of courtroom experience to every case.",
    image_url: "https://images.unsplash.com/photo-1589829085413-56de8ae18c73?auto=format&fit=crop&q=80&w=2000",
    features: ["Commercial Disputes", "Contract Disputes", "Property Disputes", "Debt Recovery", "Injunctions & Court Orders", "Alternative Dispute Resolution"]
  },
  {
    id: "maritime",
    icon: "Anchor",
    title: "Maritime Law",
    description: "Expertise in international shipping, logistics, and admiralty law. Specialized knowledge in Jamaica's maritime regulations and international conventions.",
    image_url: "https://images.unsplash.com/photo-1566839352697-362243e86c99?auto=format&fit=crop&q=80&w=1000",
    features: ["Shipping & Cargo Claims", "Charter Party Disputes", "Marine Insurance", "Port & Terminal Operations", "Vessel Registration", "International Maritime Compliance"]
  },
  {
    id: "real-estate",
    icon: "Home",
    title: "Real Estate Law",
    description: "Securing your investments in commercial and residential properties. From transactions to disputes, we protect your property interests.",
    image_url: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=1000",
    features: ["Property Transactions", "Title Searches & Due Diligence", "Lease Agreements", "Land Development", "Property Disputes", "Mortgage & Financing"]
  }
];

export const ServicesPage = () => {
  const [services, setServices] = useState(defaultServices);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const cacheBust = `?_t=${Date.now()}`;
        const response = await axios.get(`${API}/content/services${cacheBust}`);
        if (response.data && response.data.length > 0) {
          setServices(response.data);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
      }
    };
    fetchContent();
  }, []);

  return (
    <div className="p-8 md:p-12 lg:p-16">
      {/* Header */}
      <div className="text-center mb-12" data-testid="services-header">
        <p className="font-body text-xs uppercase tracking-[0.2em] text-[#C9A86C] mb-2">What We Do</p>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1a1412] mb-4">
          Our Practice Areas
        </h1>
        <p className="font-body text-[#7a7065] max-w-2xl mx-auto">
          Comprehensive legal services tailored to meet the unique needs of Jamaican businesses and individuals.
        </p>
      </div>

      {/* Services */}
      <div className="space-y-16">
        {services.map((service, index) => {
          const IconComponent = iconMap[service.icon] || Building;
          return (
            <section 
              key={index}
              className={`grid lg:grid-cols-2 gap-10 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              data-testid={`service-section-${index}`}
            >
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <div className="relative overflow-hidden rounded-xl shadow-xl group">
                  <img 
                    src={service.image_url}
                    alt={service.title}
                    className="w-full h-64 md:h-80 object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a1412]/80 to-transparent" />
                  <div className="absolute bottom-6 left-6">
                    <IconComponent className="text-[#C9A86C]" size={40} />
                  </div>
                </div>
              </div>
              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <div className="flex items-center gap-3 mb-4">
                  <IconComponent className="text-[#C9A86C]" size={32} />
                  <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1a1412]">
                    {service.title}
                  </h2>
                </div>
                <p className="font-body text-[#5a4a3a] mb-6 leading-relaxed">
                  {service.description}
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {(service.features || []).map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center gap-2">
                      <CheckCircle className="text-[#C9A86C] flex-shrink-0" size={16} />
                      <span className="font-body text-sm text-[#5a4a3a]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Link 
                  to="/book" 
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid={`service-cta-${index}`}
                >
                  Discuss Your Needs <ArrowRight size={16} />
                </Link>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="mt-20 bg-[#1a1412] rounded-xl p-10 md:p-14 text-center" data-testid="services-cta">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white mb-4">
          Not Sure Which Service You Need?
        </h2>
        <p className="font-body text-white/70 mb-8 max-w-xl mx-auto">
          Schedule a consultation and let us help you identify the best legal approach for your situation.
        </p>
        <Link 
          to="/book" 
          className="btn-primary inline-block"
          data-testid="services-book-btn"
        >
          Book a Consultation
        </Link>
      </section>
    </div>
  );
};

export default ServicesPage;
